import { isCancel } from 'axios'
import { copy } from 'copy-anything'
import { makeAutoObservable, runInAction, toJS } from 'mobx'

import {
  Hotel,
  HotelSearch,
  SearchToursApi,
  TourDepartureCity,
} from 'shared/api/tours/search'
import { simpleIsEqual } from 'shared/lib/simpleIsEqual.ts'

import formTourist from '../form-tourist/form-tourist'
import searchTour from '../search-tour/search-tour'

type TourSearchRequest = Omit<HotelSearch, 'ages'> & {
  ages?: string[] | number[] | null
}

export class TourListStore {
  listOfTours: Hotel[] | [] = []
  isLoading: boolean = false
  abortController: AbortController | null = null
  lastRequestData: TourSearchRequest | null = null
  uid: string | null = null

  hasSearchSucceeded = false
  hasShownAlmatyOffer = false
  error: unknown = null
  departureCities: TourDepartureCity[] = []

  constructor() {
    makeAutoObservable(this)
  }

  async requestToursList(preparedData: TourSearchRequest) {
    if (this.isLoading) return

    this.lastRequestData = copy(toJS(preparedData))
    this.hasSearchSucceeded = false
    this.error = null
    this.departureCities = []
    this.isLoading = true
    const controller = new AbortController()
    this.abortController = controller

    // The directory must not extend the hotel search loading time.
    void SearchToursApi.getDepartureCities()
      .then(({ data }) => {
        if (this.abortController !== controller || controller.signal.aborted)
          return
        runInAction(() => {
          this.departureCities = data.data ?? []
        })
      })
      .catch(() => {})

    try {
      const { data: toursData } = await SearchToursApi.getHotelsList(
        { ...preparedData } as HotelSearch,
        controller.signal,
      )
      if (this.abortController !== controller || controller.signal.aborted)
        return

      runInAction(() => {
        this.setUid(toursData.uid)
        this.setListOfTour(toursData.data || [])
        this.hasSearchSucceeded = true
        if (formTourist.formTourist !== null) formTourist.clearValuesForm()
      })
    } catch (error) {
      if (
        this.abortController !== controller ||
        controller.signal.aborted ||
        isCancel(error)
      )
        return
      runInAction(() => {
        this.error = error
      })
    } finally {
      if (this.abortController === controller) {
        runInAction(() => {
          this.isLoading = false
        })
      }
    }
  }

  get almatyDeparture() {
    if (!this.hasSearchSucceeded || this.isLoading || this.listOfTours.length)
      return undefined
    const bishkek = this.departureCities.find(({ name }) =>
      ['бишкек', 'bishkek'].includes(name.trim().toLowerCase()),
    )
    if (!bishkek || this.lastRequestData?.departure_city_id !== bishkek.id)
      return undefined
    return this.departureCities.find(({ name }) =>
      ['алматы', 'almaty'].includes(name.trim().toLowerCase()),
    )
  }

  markAlmatyOfferShown() {
    this.hasShownAlmatyOffer = true
  }

  async retryFromAlmaty() {
    const departure = this.almatyDeparture
    if (!departure || !this.lastRequestData || searchTour.getIsSearchEditing())
      return
    const request = {
      ...toJS(this.lastRequestData),
      departure_city_id: departure.id,
    }
    searchTour.setDataForSearchTour({ departure: toJS(departure) })
    await this.requestToursList(request)
  }

  setListOfTour(tours: Hotel[]) {
    this.listOfTours = tours
  }

  setUid(uid: string) {
    this.uid = uid
  }

  getUid() {
    return toJS(this.uid)
  }

  getTourList() {
    return toJS(this.listOfTours)
  }

  resetTourList() {
    this.hasSearchSucceeded = false
    this.hasShownAlmatyOffer = false
    this.error = null
    this.departureCities = []
    this.listOfTours = []
    this.lastRequestData = null
    this.uid = null

    if (this.abortController) {
      this.abortController.abort()
    }

    this.abortController = null
    this.isLoading = false
  }

  isNewSearch(currentRequestData: TourSearchRequest) {
    return !simpleIsEqual(this.lastRequestData, currentRequestData)
  }
}

export default new TourListStore()
